/**
 * 💰 PocketMoneyIndicator - SOLD TOTAL DISPONIBIL indicator
 * 
 * FEATURES:
 * ✅ Arată câți bani ai EFECTIV în cont ACUM (all time balance)
 * ✅ Live updates când completezi sau modifici tranzacții
 * ✅ Color-coded: verde pentru pozitiv, roșu pentru negativ
 * ✅ Mobile-optimized cu icon + sumă compactă
 * ✅ Privacy toggle pentru ascunderea sumei
 * ✅ Click pentru detailed breakdown (tooltip/modal)
 * 
 * USAGE:
 * <PocketMoneyIndicator />
 */

import React, { useState } from 'react';
import { TrendingUp, TrendingDown, Wallet, Loader2, AlertCircle, Eye, EyeOff } from 'lucide-react';
import { cn } from '../../styles/cva-v2';
import { useCurrentMonthBalance } from '../../hooks/useCurrentMonthBalance';
import { format } from 'date-fns';
import { ro } from 'date-fns/locale';

interface PocketMoneyIndicatorProps {
  className?: string;
  showBreakdown?: boolean;
  compact?: boolean;
}

export const PocketMoneyIndicator = ({
  className,
  showBreakdown = false,
  compact = false
}: PocketMoneyIndicatorProps) => {
  const [isBreakdownVisible, setIsBreakdownVisible] = useState(showBreakdown);
  const [isAmountHidden, setIsAmountHidden] = useState(false);
  const { balance, breakdown, isLoading, error } = useCurrentMonthBalance();

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('ro-RO', {
      style: 'currency',
      currency: 'RON',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatMonth = (month: string) => {
    try {
      const date = new Date(`${month}-01`);
      return format(date, 'MMMM yyyy', { locale: ro });
    } catch {
      return month;
    }
  };

  // Loading state
  if (isLoading) {
    return (
      <div className={cn(
        "flex items-center gap-2 px-3 py-2 rounded-lg bg-gray-50 dark:bg-gray-800",
        className
      )}>
        <Loader2 className="h-4 w-4 animate-spin text-gray-500" />
        {!compact && <span className="text-sm text-gray-500">Calculez...</span>}
      </div>
    );
  }

  // Error state - fail silently cu icon discret
  if (error) {
    return (
      <div className={cn(
        "flex items-center gap-2 px-3 py-2 rounded-lg bg-red-50 dark:bg-red-900/20",
        className
      )}>
        <AlertCircle className="h-4 w-4 text-red-500" />
        {!compact && <span className="text-sm text-red-600">Eroare</span>}
      </div>
    );
  }

  // Determine colors și icon based on balance
  const isPositive = balance >= 0;
  const isZero = balance === 0;
  
  const getColorClasses = () => {
    if (isZero) return 'text-gray-600 dark:text-gray-400';
    return isPositive 
      ? 'text-green-600 dark:text-green-400' 
      : 'text-red-600 dark:text-red-400';
  };

  const getBgClasses = () => {
    if (isZero) return 'bg-gray-50 dark:bg-gray-800';
    return isPositive 
      ? 'bg-green-50 dark:bg-green-900/20' 
      : 'bg-red-50 dark:bg-red-900/20';
  };

  const getIcon = () => {
    if (isZero) return Wallet;
    return isPositive ? TrendingUp : TrendingDown;
  };

  const Icon = getIcon();
  const colorClasses = getColorClasses();
  const bgClasses = getBgClasses();

  return (
    <div className={cn(
      "flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-colors",
      bgClasses,
      className
    )}
    title="Sold disponibil real - include doar tranzacțiile finalizate. Pentru sold planificat vezi Monthly Grid."
    >
      {/* Icon și Main Amount */}
      <div className="flex items-center gap-2">
        <Icon className={cn("h-4 w-4", colorClasses)} />
        
        <div className="flex items-center gap-1">
          {isAmountHidden ? (
            <span className={cn("text-sm font-semibold", colorClasses)}>
              •••••
            </span>
          ) : (
            <span className={cn("text-sm font-semibold", colorClasses)}>
              {formatCurrency(balance)}
            </span>
          )}
          
          {/* Label pentru claritate */}
          <span className="text-xs text-gray-500 dark:text-gray-400 ml-1">
            (Disponibil)
          </span>
          
          {/* Privacy toggle pentru sensitive amount */}
          <button
            onClick={() => setIsAmountHidden(!isAmountHidden)}
            className={cn(
              "p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors",
              "focus:outline-none focus:ring-2 focus:ring-gray-500/20"
            )}
            title={isAmountHidden ? "Arată suma" : "Ascunde suma"}
          >
            {isAmountHidden ? (
              <Eye className="h-3 w-3 text-gray-500" />
            ) : (
              <EyeOff className="h-3 w-3 text-gray-500" />
            )}
          </button>
        </div>
      </div>

      {/* Breakdown toggle button */}
      {!compact && (
        <button
          onClick={() => setIsBreakdownVisible(!isBreakdownVisible)}
          className={cn(
            "text-xs px-2 py-1 rounded bg-white/50 dark:bg-black/20",
            "hover:bg-white/80 dark:hover:bg-black/40 transition-colors",
            "focus:outline-none focus:ring-2 focus:ring-gray-500/20",
            colorClasses
          )}
          title="Afișează detalii"
        >
          {isBreakdownVisible ? 'Ascunde' : 'Detalii'}
        </button>
      )}

      {/* Detailed Breakdown - expandabil */}
      {isBreakdownVisible && !compact && (
        <div className={cn(
          "absolute top-full left-0 mt-2 p-4 rounded-lg shadow-lg border z-50",
          "bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700",
          "min-w-64"
        )}>
          <h4 className="font-semibold text-sm mb-3 text-gray-900 dark:text-gray-100">
            💳 Sold Disponibil Real
          </h4>
          
          <div className="space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-green-600 dark:text-green-400">💰 Total Venituri:</span>
              <span className="font-medium text-green-600 dark:text-green-400">
                +{formatCurrency(breakdown.totalIncome)}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-red-600 dark:text-red-400">💸 Total Cheltuieli:</span>
              <span className="font-medium text-red-600 dark:text-red-400">
                -{formatCurrency(breakdown.totalExpense)}
              </span>
            </div>
            
            <div className="flex justify-between">
              <span className="text-blue-600 dark:text-blue-400">🏦 Total Economii:</span>
              <span className="font-medium text-blue-600 dark:text-blue-400">
                -{formatCurrency(breakdown.totalSaving)}
              </span>
            </div>
            
            <hr className="border-gray-200 dark:border-gray-600" />
            
            <div className="flex justify-between font-semibold">
              <span className="text-gray-900 dark:text-gray-100">Bani Disponibili:</span>
              <span className={colorClasses}>
                {formatCurrency(balance)}
              </span>
            </div>
            
            <div className="text-xs text-gray-500 dark:text-gray-400 mt-2 space-y-1">
              <div>💵 Include doar tranzacțiile FINALIZATE</div>
              <div>📅 Pentru sold planificat vezi Monthly Grid</div>
              <div>ℹ️ Diferența = tranzacții în așteptare</div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PocketMoneyIndicator;